import api from "@/services/apiService";
import {ExistingDayReservation, Reservation, ReservationAPIInterface, ReservationInterface} from "@/entity/Reservation";
import {CurrentReservationObject} from "@/views/reservation-detail/ReservationDetail";
import moment from "moment";
import DatetimeExtend from "@/utils/DatetimeExtend";
import {WrapperStatus} from "@/types/WrapperConfig";
import {QuotaLineWeek} from "@/types/QuotaLineConfig";


export default class ReservationService {

  //
  // GESTION AVEC LA BASE
  public static async getReservation(id: number): Promise<ReservationAPIInterface> {
    const url = 'reservations/' + id;
    return await api.authRequest.get(url);
  }

  public static async getLinkedReservationByRencurrenceGroupId(recurrenceGroupId: string, mode: 'all' | 'new' | 'edit' | 'view' = 'all') {
    if (recurrenceGroupId !== undefined) {
      let url = 'reservations?order[startDate]=asc&recurrenceGroupId[]=' + recurrenceGroupId + '&status[]=1&page=1&itemsPerPage=999';
      const currentDate = moment().format('MM/DD/YYYY HH:mm');
      if (mode === 'edit') url += '&startDate[strictly_after]=' + currentDate;
      if (mode === 'view') url += '&startDate[before]=' + currentDate;
      return api.authRequest.get(url)
        .then(((response: any) => {
          return response;
        }));
    }
  }

  public static postReservations(newReservations: ReservationInterface[]) {
    const promises = [];
    let recurrenceId = undefined;
    if (newReservations.length > 1) recurrenceId = ReservationService.createUniqId();
    for (const reservation of newReservations) {
      if (recurrenceId) reservation.recurrenceGroupId = recurrenceId;
      reservation.convertStartAndEndDateForDataBase();
      let promise = api.authRequest.post('reservations', reservation);
      promises.push(promise);
    }
    return Promise.all(promises)
  }

  public static putReservations(reservations: ReservationInterface[]) {
    const promises = [];
    for (const reservation of reservations) {
      const newReservation = new Reservation(reservation);
      newReservation.removeOwner();
      newReservation.convertStartAndEndDateForDataBase();
      let promise = api.authRequest.put('reservations/' + reservation.id, newReservation);
      promises.push(promise);
    }
    return Promise.all(promises)
  }

  // Fake delete en changeant le status à 0
  public static deleteReservations(reservations: ReservationInterface[]) {
    const promises = [];
    for (const reservation of reservations) {
      const deletedReservation = new Reservation();
      deletedReservation.id = reservation.id;
      deletedReservation.status = 0;
      deletedReservation.log = reservation.log;
      promises.push(api.authRequest.put('reservations/' + reservation.id, deletedReservation));
    }
    return Promise.all(promises);
  };

  //
  // TRANSFORMATION DE FORMATS
  public static formatArrayBackToFront(reservations: ReservationInterface[] | ReservationAPIInterface[]): CurrentReservationObject[] {
    const formattedReservations: CurrentReservationObject[] = [];
    for (const reservation of reservations) {
      formattedReservations.push(ReservationService.formatBackToFront(reservation));
    }
    return formattedReservations;
  }


  public static formatBackToFront(reservation?: ReservationInterface | ReservationAPIInterface): CurrentReservationObject {
    let heureDebut = '';
    let day = "";
    let year = '';
    let weekNumber = '';
    if (typeof reservation?.startDate !== "undefined") {
      if (typeof reservation?.startDate === "string") {
        heureDebut = reservation.startDate.slice(11, 16);
        day = reservation.startDate.slice(0, 10);
        weekNumber = moment(day).format('WW');
        year = reservation.startDate.slice(0, 4);
      } else if (typeof reservation?.startDate === "object") {
        const startDate = new DatetimeExtend(reservation.startDate);
        heureDebut = startDate.getFullHours() + ':' + startDate.getFullMinutes();
        day = startDate.getFullYear() + '-' + startDate.getFullMonth() + '-' + startDate.getFullDate();
        weekNumber = moment(day).format('WW');
        year = startDate.getFullYear();
      }
    }

    let heureFin = '';
    if (typeof reservation?.endDate !== "undefined") {
      if (typeof reservation?.endDate === "string") {
        heureFin = reservation.endDate.slice(11, 16);
      } else if (typeof reservation?.endDate === "object") {
        const endDate = new DatetimeExtend(reservation.endDate);
        heureFin = endDate.getFullHours() + ':' + endDate.getFullMinutes();
      }
    }

    return {
      id: (typeof reservation?.id !== "undefined") ? reservation.id : 0,
      status: (typeof reservation?.status !== "undefined") ? reservation.status : 1,
      name: (typeof reservation?.name !== "undefined") ? reservation.name : "",
      // @ts-ignore  // TODO: Voir si on peut éviter le ts-ignore
      room: (typeof reservation?.room !== "undefined") ? reservation.room : "/api/rooms/1",
      // @ts-ignore // TODO: Voir si on peut éviter le ts-ignore
      owner: (typeof reservation?.owner !== "undefined") ? reservation.owner : undefined,
      recurrenceGroupId: (typeof reservation?.recurrenceGroupId !== "undefined") ? reservation.recurrenceGroupId : undefined,
      heureDebut: heureDebut,
      heureFin: heureFin,
      day: day,
      justification: (typeof reservation?.log !== "undefined") ? reservation.log : "",
      wrapperConfig: {
        status: WrapperStatus.Loading,
        text: 'Récurrence',
      },
      indexId: ReservationService.createUniqId(),
      isPristine: {
        name: true,
        day: true,
        heureDebut: true,
        heureFin: true,
      },
      intervals: {
        name: 0,
        day: 0,
        heureDebut: 0,
        heureFin: 0,
      },
      errors: {
        name: "",
        day: "",
        heures: "",
      },
      warnings: {
        name: "",
        day: "",
        heures: "",
      },
      hash: undefined,
      pendingReservations: {
        pending: 0,
        list: [],
        isIntervalFree: true,
      },
      quotaLineConfig: {
        mode: 'light',
        weekNumber: weekNumber,
        year: year,
      },
      previousDate:  {
        weekNumber: '',
        year: '',
        day: '',
      },
    };
  }

  public static newReservation(): CurrentReservationObject {
    return ReservationService.formatBackToFront();
  }

  public static formatArrayFrontToBack(reservations: CurrentReservationObject[]): ReservationInterface[] {
    let formattedReservations: ReservationInterface[] = [];
    for (const reservation of reservations) {
      formattedReservations.push(ReservationService.formatFrontToBack(reservation));
    }
    return formattedReservations;
  }

  public static formatArrayFrontToBackNotPristine(reservations: CurrentReservationObject[]): ReservationInterface[] {
    let formattedReservations: ReservationInterface[] = [];
    for (const reservation of reservations) {
      if (ReservationService.isReservationPristine(reservation)) {
        formattedReservations.push(ReservationService.formatFrontToBack(reservation));
      }
    }
    return formattedReservations;
  }

  public static isReservationPristine(reservation: CurrentReservationObject): boolean {
    for (const index in reservation.isPristine) {
      if (typeof reservation.isPristine[index] === "boolean" && !reservation.isPristine[index]) return false;
    }
    return true;
  }

  // Nouvelle version de isReservationPristine, il faudrait tout faire passer sur cette fonction là, elles ne renvoyent pas la même valeur mais vérifie la même chose
  public static isReservationHasNoPristine(reservation: CurrentReservationObject): boolean {
    for (const index in reservation.isPristine) {
      if (typeof reservation.isPristine[index] === "boolean" && reservation.isPristine[index]) return false;
    }
    return true;
  }

  public static formatFrontToBack(reservation: CurrentReservationObject): ReservationInterface {
    const formattedReservation: ReservationInterface = new Reservation();
    if (reservation.id !== 0) formattedReservation.id = reservation.id;
    formattedReservation.name = reservation.name;
    const [startHour, startMinute] = reservation.heureDebut.split(":");
    const [endHour, endMinute] = reservation.heureFin.split(":");
    formattedReservation.startDate = new Date(reservation.day);
    formattedReservation.startDate.setHours(Number(startHour), Number(startMinute));
    formattedReservation.endDate = new Date(reservation.day);
    formattedReservation.endDate.setHours(Number(endHour), Number(endMinute));
    formattedReservation.recurrenceGroupId = reservation.recurrenceGroupId;
    formattedReservation.log = reservation.justification;
    formattedReservation.status = 1;
    formattedReservation.room = "/api/rooms/1"; // TODO: A définir, voir comment gérer ça, on laisse comme ça pour le moment
    return formattedReservation;
  }

  public static createUniqId(): string {
    return (Date.now().toString(36) + Math.random().toString(36).substr(2, 9)).toUpperCase();
  }

  public static hasBeenModifiedByHash(reservations: CurrentReservationObject[]): boolean {
    for (const reservation of reservations) {
      if (reservation.hash !== ReservationService.hashReservation(reservation)) return true;
    }
    return false;
  }

  // Vérification si modifications avec hash
  public static getEditedReservationsByHash(reservations: CurrentReservationObject[]): CurrentReservationObject[] {
    const editedReservations: CurrentReservationObject[] = [];
    for (const reservation of reservations) {
      if (reservation.hash !== ReservationService.hashReservation(reservation)) editedReservations.push(reservation);
    }
    return editedReservations;
  }

  public static hashReservation(reservation: CurrentReservationObject): number {
    const reservationForm = {
      name: reservation.name,
      day: reservation.day,
      heureDebut: reservation.heureDebut,
      heureFin: reservation.heureFin,
    };
    const string = JSON.stringify(reservationForm);
    return ReservationService.hashString(string);
  }

  public static hashString(s: string): number {
    for (var i = 0, h = 0; i < s.length; i++)
      h = Math.imul(31, h) + s.charCodeAt(i) | 0;
    return h;
  }

  public static simulateNewQuota(reservations: CurrentReservationObject[]): Record<string, number> {
    const quotaByWeeks: Record<string, number> = {};
    for (const reservation of reservations) {
      if (!ReservationService.isReservationValid(reservation)) continue;

      const day = moment(reservation.day, 'YYYY-MM-DD');
      const weekNumber = day.format('WW') + '-' + day.format('YYYY');

      let quotaCounter = 0;
      const [startHour, startMinute] = reservation.heureDebut.split(':');
      const [endHour, endMinute] = reservation.heureFin.split(':');
      quotaCounter += parseInt(endHour) - parseInt(startHour);
      quotaCounter += parseInt(endMinute) / 60 - parseInt(startMinute) / 60;

      const previousAdded = quotaByWeeks[weekNumber] ? quotaByWeeks[weekNumber] : 0;
      quotaByWeeks[weekNumber] = previousAdded + quotaCounter;
    }
    return quotaByWeeks;
  }

  // /!\ Ne vérifie que 3 types d'erreurs sur 5 & doit être précédé d'une update d'erreur - ne garantie pas la validité totale
  public static isReservationValid(reservation: CurrentReservationObject): boolean {
    const isPendingEnded = !(reservation.pendingReservations.pending > 0);
    const isIntervalFree = reservation.pendingReservations.isIntervalFree;
    const isFieldsValid = !(reservation.errors.name.length > 0 || reservation.errors.day.length > 0 || reservation.errors.heures.length > 0);

    return isPendingEnded && isIntervalFree && isFieldsValid;
  }

  // Update Justification when loading recurrences & validating modal
  public static isJustificationNeeded(reservations: ReservationAPIInterface[]): boolean {
    const limitJustificationNeeded = moment().startOf('day').add(2, 'days');
    for (const reservation of reservations) {
      if (limitJustificationNeeded > moment(reservation.startDate)) return true;
    }
    return false;
  }

  public static isJustificationNeededForReservation(reservation: ReservationAPIInterface): boolean {
    const limitJustificationNeeded = moment().startOf('day').add(2, 'days');
    return limitJustificationNeeded > moment(reservation.startDate);
  }

  public static async getDayReservation(day: moment.Moment): Promise<ExistingDayReservation[]> {
    return await api.authRequest.get('reservations/available/' + day.format('YYYY-MM-DD') + '/' + day.format('Z'));
  }

  public static getReservationsApiFromReservations(reservations: ReservationInterface[], oldReservations: ReservationAPIInterface[]): ReservationAPIInterface[] {
    const editedReservationsApi: ReservationAPIInterface[] = [];
    for (const reserv of reservations) {
      const oldReservation = oldReservations.find((oldReserv) => {
        return oldReserv.id === reserv.id;
      });
      if (oldReservation) editedReservationsApi.push(oldReservation);
    }
    return editedReservationsApi;
  }


}
